import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Highlighter from 'react-highlight-words'
import { SearchOutlined } from '@ant-design/icons'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import {
  Button,
  Input,
  InputRef,
  Space,
  Table,
  TableColumnsType,
  TableColumnType,
} from 'antd'
import { path } from 'src/libs'
import { Guard } from 'src/features/session/guard'
import { AddTicketsModal } from 'src/pages/goldenMay2026/tickets/widgets'
import { useTicketsList } from 'src/pages/goldenMay2026/tickets/hooks'
import {
  changeStatusSubmit,
  ticketsListLoad,
} from 'src/pages/goldenMay2026/tickets/model'
import { Ticket } from 'src/pages/goldenMay2026/tickets/types'

interface DataType {
  userID: number
  ticketID: number
  isUsed: boolean
}
type DataIndex = keyof DataType

export const TicketsList = () => {
  const [isAddTicketsModalOpen, setIsAddTicketsModalOpen] = useState(false)
  const { tickets, totalCountOfTickets, loading } = useTicketsList()
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  useEffect(() => {
    ticketsListLoad()
  }, [])

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex,
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText((selectedKeys as string[])[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: TableColumnsType<Ticket> = [
    {
      title: 'Ticket',
      dataIndex: 'ticketID',
      key: 'ticketID',
      sorter: (a, b) => a.ticketID - b.ticketID,
      ...getColumnSearchProps('ticketID'),
    },
    {
      title: 'User ID',
      dataIndex: 'userID',
      key: 'userID',
      sorter: (a, b) => a.userID - b.userID,
      ...getColumnSearchProps('userID'),
      render: (id) => (
        <Link
          to={path.clients.details(id)}
          style={{ textDecoration: 'none' }}
          target="_blank"
        >
          {id}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isUsed',
      filters: [
        {
          text: 'Reserved',
          value: false,
        },
        {
          text: 'Issues',
          value: true,
        },
      ],
      onFilter: (value, record) => record.isUsed == value,
      render: (text, record) => {
        if (record.isUsed) {
          return 'Issued'
        }
        return 'Reserved'
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: Ticket) => {
        return (
          !item.isUsed && (
            <Guard permission="golden_may_2026_ban">
              <Button
                size="small"
                color="danger"
                variant="solid"
                onClick={() => {
                  if (window.confirm('Are you sure?')) {
                    changeStatusSubmit({ userID: item.userID })
                  }
                }}
              >
                Issue a ticket
              </Button>
            </Guard>
          )
        )
      },
    },
  ]

  return (
    <>
      <Guard permission="reserve_ticket_gm26">
        <Button
          color="geekblue"
          type="primary"
          size="large"
          style={{ alignSelf: 'flex-start', marginBottom: 24 }}
          onClick={() => setIsAddTicketsModalOpen(true)}
        >
          Ticket reservation
        </Button>
      </Guard>
      <Table<Ticket>
        dataSource={tickets}
        bordered={true}
        className="overflow-auto"
        loading={loading}
        rowKey="ticketID"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfTickets,
        }}
      />
      <AddTicketsModal
        isVisible={isAddTicketsModalOpen}
        onClose={() => setIsAddTicketsModalOpen(false)}
      />
    </>
  )
}
